import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { ISubscriptionResponse } from "@miplatform/types";
import { getRootUrl } from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { useBidPrintHeaderViewModel } from "./useBidPrintHeaderViewModel";

export const BidPrintHeader = ({ tenant }: BidPrintHeaderProps) => {
  const API_URL = getRootUrl();
  const { classes, cx } = useStyles();
  const {
    name,
    pipeBrand,
    builderName,
    collectionSeries,
    subdivision,
    headerDescription,
    requestDate,
    seriesGroupId,
    builderId,
    getSeries,
  } = useBidPrintHeaderViewModel();

  useEffect(() => {
    if (builderId) {
      const seriesGroupIdFilter = seriesGroupId
        ? { type: "guid", value: seriesGroupId }
        : { type: "guid", value: null };
      getSeries({
        filters: {
          accountId: { type: "guid", value: builderId },
          seriesGroupId: seriesGroupIdFilter,
        },
      });
    }
  }, [builderId, seriesGroupId]);

  return (
    <Box className={classes.root}>
      <Box className={classes.topSection}>
        {tenant?.logo && (
          <img
            src={`${API_URL}/${tenant.logo.referenceId}`}
            alt="Company Logo"
            className={classes.logo}
          />
        )}
        <Typography className={classes.companyName}>
          Plumbing Information Sheet
        </Typography>
      </Box>
      <Box className={classes.columns}>
        <Box className={classes.leftColumn}>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Pex</Typography>
            <Typography className={classes.value}>
              {pipeBrand || "--"}
            </Typography>
          </Box>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Address</Typography>
            <Typography className={classes.value}>--</Typography>
          </Box>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Plan#</Typography>
            <Typography className={classes.value}>{name || "--"}</Typography>
          </Box>
        </Box>
        <Box className={classes.centerColumn}>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Builder</Typography>
            <Typography className={classes.value}>
              {builderName || "--"}
            </Typography>
          </Box>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Collection/Series</Typography>
            <Typography className={classes.value}>
              {collectionSeries || "--"}
            </Typography>
          </Box>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Subdivision</Typography>
            <Typography className={classes.value}>
              {subdivision || "--"}
            </Typography>
          </Box>
          <Box className={classes.fieldRow}>
            <Typography className={cx(classes.label, classes.labelSpacer)} />{" "}
            <Typography className={classes.value}>
              {headerDescription && (
                <Typography className={classes.description}>
                  {headerDescription || "--"}
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.rightColumn}>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Bid Send Date</Typography>
            <Typography className={classes.value}>{requestDate}</Typography>
          </Box>
          <Box className={classes.fieldRow}>
            <Typography className={classes.label}>Revision Date</Typography>
            <Typography className={classes.value}>--/--/----</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  topSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  logo: {
    height: 40,
    objectFit: "contain",
  },
  companyName: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.fontWeight.bold,
    color: theme.palette.common.black,
    textAlign: "center",
  },
  columns: {
    display: "flex",
    gap: theme.spacing(4),
    alignItems: "space-between",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.fontWeight.regular,
  },
  leftColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    minWidth: 140,
    flexShrink: 0,
    flex: 1,
  },
  centerColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    flex: 1,
  },
  rightColumn: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    minWidth: 160,
    flexShrink: 0,
    alignItems: "flex-end",
  },
  fieldRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "baseline",
  },
  label: {
    fontSize: "inherit",
    color: theme.palette.grey.medium,
    whiteSpace: "nowrap",
  },
  labelSpacer: {
    minWidth: 64,
  },
  value: {
    fontSize: "inherit",
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.text.primary,
  },
  description: {
    fontSize: "inherit",
    fontWeight: theme.fontWeight.regular,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
  },
}));

interface BidPrintHeaderProps {
  tenant?: ISubscriptionResponse | null;
}
